import jsonExport from 'jsonexport/dist';
import { downloadCSV, Exporter, useResourceContext } from 'react-admin';

import { moneyFormatter, dateFormatter, LocalStorage } from '@utils';
import { CreditStatus } from '@types';

type TransformFn = (value: string | number | any) => string;

interface ExportOption {
  label?: string;
  prop?: string;
  sourceUsd?: string;
  transform?: 'money' | 'moneyAndEmpty' | 'moneyNullable' | 'date' | 'datetime' | 'dateAndEmpty'
    | 'phone' | 'boolean' | 'done' | 'documentsFilled' | 'xPostfix' | 'masked'
    | 'uppercase' | 'percent' | 'files' | 'spaced' | 'parDelayFixed' | 'minutes'
    | 'percentAndEmpty' | 'array' | 'comments' | 'status' | 'currency' | TransformFn;
  manyProp?: string;
  hide?: boolean;
  postfix?: string;
}

interface Options {
  props: {[key: string]: ExportOption};
  filename: string;
  extraRequest?: any;
}

type OptionsMake = Options & {resource: string};

export const useExporter = ({ props, filename, extraRequest }: Options): Exporter => {
  const resource = useResourceContext();

  return makeExporter({ props, filename, extraRequest, resource });
};

export const makeExporter = (optionsMake: OptionsMake): Exporter => {
  const { props, filename, extraRequest, resource } = optionsMake;
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5777';

  const exportRecords = async (records: any[]) => {
    const selection = LocalStorage.get(`${resource}`);
    const selectionLength = Object.keys(selection || {}).length;

    const mappedRecords = records.map(record => {
      const isUsd = record.isUsd ? true : false;
      const exportObj: {[key: string]: string | number} = {};

      for (const [key, option] of Object.entries(props)) {
        if (option.hide) continue;

        if (selectionLength === 0 || selection[key]) {
          const source = option.sourceUsd && isUsd ? option.sourceUsd : (option.prop || key);
          let value = recompose(record, source);

          if (typeof option.manyProp === 'string') {
            value = (value || [])
              .map((v: any) => v[option.manyProp as string])
              .join(', ');
          }
          if (option.transform === 'money') {
            value = moneyFormatter.format(value || 0);
          }
          if (option.transform === 'moneyNullable') {
            value = value === undefined || value === null
              ? undefined
              : moneyFormatter.format(value || 0);
          }
          if (option.transform === 'moneyAndEmpty') {
            value = /^[0-9]+(\.[0-9]+)?$/.test(value) ? moneyFormatter.format(+value) : 'N/A';
          }
          if (option.transform === 'date') {
            value = value ? dateFormatter.toDateSlash(value) : null;
          }
          if (option.transform === 'dateAndEmpty') {
            value = value ? dateFormatter.toDateSlash(value) : 'N/A';
          }
          if (option.transform === 'datetime') {
            value = value ? dateFormatter.toDateSlash(value, true) : null;
          }
          if (option.transform === 'phone') {
            value = value ? phoneTransformer(value) : null;
          }
          if (option.transform === 'boolean') {
            value = value ? 'yes' : 'no';
          }
          if (option.transform === 'done') {
            value = (value === null || value === undefined || value === '')
              ? ''
              : value ? 'Done' : 'Pending';
          }
          if (option.transform === 'documentsFilled') {
            value = value && value.length ? 'Complete' : 'Empty';
          }
          if (option.transform === 'uppercase' && value) {
            const strValue = `${value}`;
            value = strValue.charAt(0).toUpperCase() + strValue.slice(1);
          }
          if (option.transform === 'percent') {
            value = `${value || 0}%`;
          }
          if (option.transform === 'percentAndEmpty') {
            value = value === null ? '' : `${value}%`;
          }
          if (option.transform === 'parDelayFixed') {
            value = record.parDelayEdited ? `${value} / manual` : value;
          }
          if (option.transform === 'masked' && value) {
            value = '*'.repeat(`${value}`.length);
          }
          if (option.transform === 'minutes' && value && typeof value === 'string') {
            value = dateFormatter.parseToMin(value);
          }
          if (option.transform === 'array' && Array.isArray(value)) {
            value = value.map(v => `${v}`).join(', ');
          }
          if (option.transform === 'files' && Array.isArray(value)) {
            const links: string[] = [];
            value.forEach((v: any) => {
              if (v?.adminUrl) {
                links.push(`${apiUrl}${v.adminUrl}`);
              } else if (v?.file?.adminUrl) {
                links.push(`${apiUrl}${v.file.adminUrl}`);
              }
            });
            const isMac = navigator.userAgent.includes('Mac');
            value = links.join(isMac ? '\n' : ' ');
          }
          if (option.transform === 'comments' && Array.isArray(value)) {
            const comments: string[] = value.map((v: any) => {
              return `${v.adminName} (${dateFormatter.toDateSlash(v.createdAt, true)}): ${v.comment}`;
            });
            value = comments.join('; ');
          }
          if (option.transform === 'spaced' && value) {
            value = `="${value}"`;
          }
          if (option.transform === 'status') {
            switch (value) {
              case CreditStatus.MODERATION:
                value = 'Applying';
                break;
              case CreditStatus.COMPLETED_FILE:
                value = 'Completed File';
                break;
              case CreditStatus.BANK_REVISION:
                value = 'Completed File';
                break;
              case CreditStatus.APPROVED:
                value = 'Completed File';
                break;
              case CreditStatus.DENIED:
                value = 'Completed File';
                break;
              case CreditStatus.SIGNED:
                value = 'Denied';
                break;
              case CreditStatus.REPAID:
                value = 'Repaid';
                break;
              case CreditStatus.DRAFT:
                value = 'Draft';
                break;
              case CreditStatus.RESTRUCTURE:
                value = 'Restructure';
                break;
              case CreditStatus.DELAY:
                value = 'Delay';
                break;
              default:
                value = 'Unknown/Airtable';
                break;
            }
          }
          if (option.transform === 'currency') {
            value = value ? 'USD' : 'MXN';
          }
          if (typeof option.transform === 'function') {
            value = option.transform(value);
          }
          if (option.postfix) {
            value = `${value || ''}${option.postfix}`;
          }
          if (option.sourceUsd && isUsd) {
            value = `${value || ''} USD`;
          }
          const exportKey = option.label || key;
          exportObj[exportKey] = value;
        }
      }

      return exportObj;
    });

    jsonExport(mappedRecords, {}, (err, csv) => {
      downloadCSV(csv, filename);
    });
  };

  const exporter: Exporter = async (records: any[]) => {
    if (!Array.isArray(records)) {
      return;
    }

    if (extraRequest && records?.length > 0) {
      try {
        await extraRequest(records);
      } catch (error) {
        console.error('Extra request failed:', error);
      }
    }

    await exportRecords(records);
  };

  return exporter;
};

function recompose(obj: any, string: any): any {
  if (obj === null || obj === undefined) {
    return null;
  }

  const parts = string.split('.');
  const newObj = obj[parts[0]];

  if (parts[1]) {
    parts.splice(0, 1);
    const newString = parts.join('.');

    return recompose(newObj, newString);
  }

  return newObj;
}

function phoneTransformer(phone: any): string | null {
  if (!phone) {
    return null;
  }

  const countryCode = phone.slice(0, phone.length - 10);
  let f = phone.replace(/\D/g, '').slice(phone.length - 11);

  if (f[9] !== undefined) {
    f = `${countryCode
    } (${f[0]}${f[1]}${f[2]}) ${f[3]}${f[4]}${f[5]}-${
      f[6]}${f[7]}${f[8]}${f[9]}`;
  }

  return f;
}
